/**
 * useServiceOrderPhasePermissions - Permissões granulares por fase da OS
 *
 * Espelho de `useTaskPhasePermissions` para o módulo Ordem de Serviço:
 * consome a RPC `get_service_order_phase_permissions_for_user` (v1.0.0) e
 * expõe `canDoOnPhase(phaseId, action)` para o frontend listar apenas as
 * fases que o perfil do usuário pode usar como destino.
 *
 * Regras (idem Tarefas):
 * - Admins (root_admin, tenant_admin): acesso total (bypass no server-side)
 * - Demais perfis: permissões de service_order_phase_permissions
 * - Sem perfil: deny by default (RPC retorna vazio)
 * - O servidor revalida tudo em `_os_assert_phase_permission` na gravação
 *
 * @version 1.0.0
 * RELEASE: 2026-10-09 v1.0.0 - Criação inicial (atalho Alterar Fase da OS).
 */

import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { resolveServiceOrderPermission } from '@/lib/serviceOrderResponsibility';

/** Logger do módulo */
const logger = createLogger('useServiceOrderPhasePermissions');

/** Ações verificáveis por fase (mesmo conjunto de Tarefas) */
export type ServiceOrderPhaseAction =
  | 'can_view'
  | 'can_create'
  | 'can_edit'
  | 'can_delete'
  | 'can_change_phase'
  | 'can_change_status'
  | 'can_change_responsible'
  | 'can_comment'
  | 'can_reopen';

/** Registro de permissão por fase retornado pela RPC */
interface ServiceOrderPhasePermissionRow {
  phase_id: string;
  phase_name: string;
  phase_color: string;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_change_phase: boolean;
  can_change_status: boolean;
  can_change_responsible: boolean;
  can_comment: boolean;
  can_reopen: boolean;
  is_responsible_only: boolean;
}

/** Interface do retorno do hook */
export interface ServiceOrderPhasePermissions {
  /** Verifica se o usuário pode realizar uma ação em uma fase específica */
  canDoOnPhase: (phaseId: string | null | undefined, action: ServiceOrderPhaseAction, isResponsible?: boolean) => boolean;
  /** Flag indicando se as permissões estão carregando */
  isLoading: boolean;
  /** Se o usuário é admin (acesso total) */
  isAdmin: boolean;
  /** Falha mantém as ações bloqueadas. */
  isError: boolean;
}

/**
 * Hook central de permissões granulares por fase da OS.
 *
 * @param categoryId - Opcional: filtra fases por categoria; sem valor, traz
 *   as permissões de TODAS as categorias (necessário para troca de categoria).
 */
export function useServiceOrderPhasePermissions(categoryId?: string | null): ServiceOrderPhasePermissions {
  const { isAdmin, loading: permLoading } = usePermissions();
  const { user, currentTenant } = useAuth();

  // Busca permissões via RPC (cache 60s) — a RPC ainda não consta nos tipos
  // gerados, então a chamada usa `.call(supabase, ...)` para preservar o
  // receiver do cliente (regra do projeto) com cast controlado.
  const { data: rawData, isLoading: isQueryLoading, isError, isSuccess } = useQuery({
    queryKey: ['service-order-phase-permissions', currentTenant?.id, user?.id, categoryId || 'all'],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any).call(
        supabase,
        'get_service_order_phase_permissions_for_user',
        { p_category_id: categoryId || null, p_tenant_hint: currentTenant?.id ?? null },
      );

      if (error) {
        logger.error('Erro ao buscar permissões por fase da OS', error);
        throw error;
      }

      return (data || []) as ServiceOrderPhasePermissionRow[];
    },
    staleTime: 60_000, // Cache 60s
    gcTime: 5 * 60_000,
    enabled: !permLoading && !!user?.id && !!currentTenant?.id,
    refetchInterval: 30_000,
  });

  /**
   * Verifica se o usuário pode realizar uma ação em uma fase.
   * Deny by default: sem registro, retorna false.
   */
  const canDoOnPhase = useCallback(
    (phaseId: string | null | undefined, action: ServiceOrderPhaseAction, isResponsible?: boolean): boolean => {
      // Sem phase_id = deny
      if (!phaseId) return false;

      // Admin = allow all (redundante, mas seguro para evitar flash)
      if (isError || !isSuccess || permLoading || !user?.id || !currentTenant?.id) return false;
      if (isAdmin) return true;

      // Ainda carregando = deny (previne flash de conteúdo não autorizado)
      if (isQueryLoading || permLoading) return false;

      // A linha específica negativa não recebe OR da linha genérica.
      return resolveServiceOrderPermission(rawData ?? [], phaseId, action, isResponsible);
    },
    [isAdmin, isQueryLoading, permLoading, rawData, isError, isSuccess, user?.id, currentTenant?.id]
  );

  return {
    canDoOnPhase,
    isLoading: isQueryLoading || permLoading,
    isAdmin,
    isError,
  };
}
